import useMediaQuery from "@mui/material/useMediaQuery";
import { SIDE_PANEL_MIN_WIDTH, SIDE_PANEL_WIDTH } from "_shared/_views/side-panel-width";
import type {
  FeeScheduleCategoryEditorField,
  FeeScheduleCategoryEditorFocus,
} from "_shared/fee-schedules/categories/_views/fee-schedule-category-editor-focus";
import { useReducer } from "react";
import {
  fieldTraversalStop,
  ProposalTraversalStop,
  unreadCommentsTraversalStop,
} from "work/proposals/issues/proposal-traversal-stops";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

export const NEW_FEE_CATEGORY_DRAWER_KEY = "new-fee-category";

/**
 * The right-hand side panels that share the same screen real estate on the proposal page: the
 * comments drawer, the two fee-schedule-category drawers, and the vendor-team member selection drawer. Only one of them may be open at a time — see `withOpenSidePanel` below, which
 * is the only place that derives the per-panel `*Open` booleans from this field, so it is
 * structurally impossible for two of them to be true at once.
 */
export type ProposalSidePanel = "comments" | "addFee" | "existingFee" | "teamSelection";

export type ProposalPagePanelState<TCategory> = {
  selectedReviewField?: ProposalField;
  currentCommentField?: ProposalField;
  commentsTitle?: string;
  /**
   * Single source of truth for which of the mutually exclusive side panels is open. Prefer reading
   * the derived `commentsOpen`/`addFeeOpen`/`existingFeeOpen`/`teamSelectionOpen` booleans below —
   * they are always kept in sync with this field by `withOpenSidePanel`.
   */
  openSidePanel?: ProposalSidePanel;
  commentsOpen: boolean;
  addFeeOpen: boolean;
  existingFeeOpen: boolean;
  teamSelectionOpen: boolean;
  currentCategory?: TCategory;
  /**
   * The field the category editor was opened to fix, when an issue about the rate's Team Member or
   * Fee was pressed, and a new request each time, so a second press focuses the field again (#6097).
   * Opening the editor any other way clears it: the row's Edit action asks for no field.
   */
  categoryEditorFocus?: FeeScheduleCategoryEditorFocus;
  reviewerAnchor?: HTMLButtonElement;
  isManagingReviewers: boolean;
  /**
   * Where "Prev / Next" last landed, so the stop the reader is on keeps its place in the walk once
   * arriving has read it. Any other way of reaching a field or the pane clears it: the reader has
   * left the walk, and Next starts it again from the top.
   */
  traversalStop?: ProposalTraversalStop;
  /**
   * The comment an unread-comments stop scrolls the pane to — its conversation's oldest unread one when
   * it arrived, or none when that comment is not loaded yet and the pane has to load back to it.
   */
  commentToScrollTo?: string;
  /**
   * How many times the page has been sent to a field. The page scrolls to the stop whenever this or
   * the tab changes (`useTraverserStopScope`), so arriving again at the field already selected — "Next"
   * on a tab's lone issue, after scrolling away from it — still brings it back into view. Deriving the
   * scroll from the field itself would miss that, and would scroll whenever a realtime update resolved
   * the tab's first issue and the default stop moved on, which nobody asked for.
   */
  fieldArrivals: number;
};

type ProposalPagePanelAction<TCategory> =
  | {
      type: "openComments";
      field?: ProposalField;
      title?: string;
      preserveTitleWhenUndefined?: boolean;
    }
  | { type: "closeComments" }
  | {
      type: "openAddFeePanel";
      category?: TCategory;
      preserveCategory?: boolean;
      focus?: FeeScheduleCategoryEditorField;
    }
  | { type: "closeAddFeePanel"; clearCategory?: boolean }
  | { type: "openExistingFeePanel" }
  | { type: "closeExistingFeePanel" }
  | { type: "setTeamSelectionOpen"; open: boolean }
  | {
      type: "setSelectedReviewField";
      field?: ProposalField;
      closePanels?: boolean;
      openComments?: boolean;
      /** A "Prev / Next" press, as opposed to the reader reaching the field by their own hand. */
      traversed?: boolean;
    }
  | { type: "arriveAtUnreadComments"; isExternal: boolean; commentToScrollTo?: string }
  | { type: "closeTabScopedPanels" }
  | { type: "openReviewerSelector"; anchor: HTMLButtonElement }
  | { type: "closeReviewerSelector" };

const initialProposalPagePanelState: ProposalPagePanelState<never> = {
  openSidePanel: undefined,
  commentsOpen: false,
  addFeeOpen: false,
  existingFeeOpen: false,
  teamSelectionOpen: false,
  isManagingReviewers: false,
  fieldArrivals: 0,
};

export function useProposalPagePanels<TCategory>() {
  return useReducer(
    proposalPagePanelReducer<TCategory>,
    initialProposalPagePanelState as ProposalPagePanelState<TCategory>,
  );
}

/**
 * Applies a new "which side panel is open" value and re-derives the four `*Open` booleans from it.
 * This is the only place in the reducer that sets those booleans, so they can never drift out of
 * sync with each other — opening one panel is, structurally, closing every other one sharing this
 * region of the page (comments, add-fee, existing-fee, team-selection).
 */
function withOpenSidePanel<TCategory>(
  state: ProposalPagePanelState<TCategory>,
  openSidePanel: ProposalSidePanel | undefined,
): ProposalPagePanelState<TCategory> {
  return {
    ...state,
    openSidePanel,
    commentsOpen: openSidePanel === "comments",
    addFeeOpen: openSidePanel === "addFee",
    existingFeeOpen: openSidePanel === "existingFee",
    teamSelectionOpen: openSidePanel === "teamSelection",
  };
}

/** Clears the side panel only if `panel` is the one currently open; otherwise leaves state alone. */
function closeOpenSidePanel<TCategory>(
  state: ProposalPagePanelState<TCategory>,
  panel: ProposalSidePanel,
): ProposalPagePanelState<TCategory> {
  return state.openSidePanel === panel ? withOpenSidePanel(state, undefined) : state;
}

export function proposalPagePanelReducer<TCategory>(
  state: ProposalPagePanelState<TCategory>,
  action: ProposalPagePanelAction<TCategory>,
): ProposalPagePanelState<TCategory> {
  switch (action.type) {
    case "openComments": {
      const commentField = action.field ?? ProposalField.General;
      return {
        ...withOpenSidePanel(state, "comments"),
        // A manually opened thread is also the field the issue traverser and
        // highlight are currently reviewing. Keeping these separate made Next
        // jump from an unrelated auto-selected unread field.
        selectedReviewField: commentField,
        currentCommentField: commentField,
        commentsTitle:
          action.preserveTitleWhenUndefined && action.title === undefined ? state.commentsTitle : action.title,
        traversalStop: undefined,
        commentToScrollTo: undefined,
      };
    }
    case "arriveAtUnreadComments":
      // The pane as it opens by hand — on the proposal's conversations, not on any field — on the tab of
      // the conversation the stop is for, and scrolled to where its unread comments begin. A new stop
      // object for every arrival, so the pane can tell a second arrival from the first.
      return {
        ...withOpenSidePanel(state, "comments"),
        selectedReviewField: ProposalField.General,
        currentCommentField: ProposalField.General,
        commentsTitle: undefined,
        traversalStop: unreadCommentsTraversalStop(action.isExternal),
        commentToScrollTo: action.commentToScrollTo,
      };
    case "closeComments":
      return closeOpenSidePanel(state, "comments");
    case "openAddFeePanel":
      return {
        ...withOpenSidePanel(state, "addFee"),
        currentCategory: action.preserveCategory ? state.currentCategory : action.category,
        categoryEditorFocus: action.focus
          ? { field: action.focus, request: (state.categoryEditorFocus?.request ?? 0) + 1 }
          : undefined,
      };
    case "closeAddFeePanel":
      return {
        ...closeOpenSidePanel(state, "addFee"),
        currentCategory: action.clearCategory ? undefined : state.currentCategory,
      };
    case "openExistingFeePanel":
      return {
        ...withOpenSidePanel(state, "existingFee"),
        currentCategory: undefined,
      };
    case "closeExistingFeePanel":
      return closeOpenSidePanel(state, "existingFee");
    case "setTeamSelectionOpen": {
      const isCurrentlyOpen = state.openSidePanel === "teamSelection";
      if (isCurrentlyOpen === action.open) {
        return state;
      }
      return withOpenSidePanel(state, action.open ? "teamSelection" : undefined);
    }
    case "setSelectedReviewField": {
      const traversal = {
        traversalStop: action.traversed && action.field ? fieldTraversalStop(action.field) : undefined,
        commentToScrollTo: undefined,
        fieldArrivals: action.field ? state.fieldArrivals + 1 : state.fieldArrivals,
      };
      return action.openComments && action.field
        ? {
            ...withOpenSidePanel(action.closePanels ? closeTabScopedPanels(state) : state, "comments"),
            selectedReviewField: action.field,
            currentCommentField: action.field,
            commentsTitle: undefined,
            ...traversal,
          }
        : action.closePanels
          ? { ...closeTabScopedPanels(state), selectedReviewField: action.field, ...traversal }
          : { ...state, selectedReviewField: action.field, ...traversal };
    }
    case "closeTabScopedPanels":
      return closeTabScopedPanels(state);
    case "openReviewerSelector":
      return {
        ...state,
        reviewerAnchor: action.anchor,
        isManagingReviewers: true,
      };
    case "closeReviewerSelector":
      return {
        ...state,
        reviewerAnchor: undefined,
        isManagingReviewers: false,
      };
  }
}

function closeTabScopedPanels<TCategory>(
  state: ProposalPagePanelState<TCategory>,
): ProposalPagePanelState<TCategory> {
  return {
    ...withOpenSidePanel(state, undefined),
    selectedReviewField: undefined,
    currentCommentField: undefined,
    commentsTitle: undefined,
    currentCategory: undefined,
    reviewerAnchor: undefined,
    isManagingReviewers: false,
    traversalStop: undefined,
    commentToScrollTo: undefined,
  };
}

/**
 * The width a side panel asks for — a CSS length, not a number, because it narrows with the window;
 * see `side-panel-width`. The column reserved in the page's row and the drawer's own paper are both
 * given this same expression so they cannot disagree about how much of the row is spoken for.
 */
export const PROPOSAL_SIDE_PANEL_WIDTH = SIDE_PANEL_WIDTH;

/** The narrowest content column worth keeping beside a panel. */
const MINIMUM_CONTENT_WIDTH_BESIDE_SIDE_PANEL = 320;

/**
 * Whether the page's row can seat a side panel beside its content rather than over it.
 *
 * The panel's width is reserved in the row, so the content gets whatever is left — and on a 390px
 * phone that was 16px of squeezed, unreadable page behind a panel that covered it anyway (#4964).
 * Below this width the panel covers the content instead, which is what it already did before the
 * width was reserved at all, and what a full-screen drawer would do.
 *
 * The viewport is the right thing to ask here, unusually for these pages: the decision is about the
 * page's whole row, and the only other claim on it — the application's navigation drawer — is
 * permanent from `lg` up, far above the width this threshold sits at. Below `lg` the row is the
 * window.
 */
export function useProposalPageCanSeatSidePanel(): boolean {
  return useMediaQuery(
    `(min-width: ${SIDE_PANEL_MIN_WIDTH + MINIMUM_CONTENT_WIDTH_BESIDE_SIDE_PANEL}px)`,
  );
}

export function getProposalDrawerWidth(args: {
  teamSelectionOpen: boolean;
  commentsOpen: boolean;
  addFeeOpen: boolean;
  existingFeeOpen: boolean;
  /** Defaults to true so a caller that cannot measure keeps the room-reserving behaviour. */
  canSeatSidePanel?: boolean;
}): number | string {
  if (args.canSeatSidePanel === false) {
    return 0;
  }
  if (args.teamSelectionOpen) {
    return "min(500px, 100vw)";
  }
  if (args.commentsOpen || args.addFeeOpen || args.existingFeeOpen) {
    return PROPOSAL_SIDE_PANEL_WIDTH;
  }
  return 0;
}
